import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ArrowDownCircle, ArrowUpCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getUnmatchedEntries,
  launchUnmatchedEntries,
} from "@/Api/reconcialiationFinancialTransactions";
import { selectExpenseTypesData } from "@/Api/expenseTypes";
import { selectRevenueTypesData } from "@/Api/revenueTypes";
import { getSelectUnitiesOnlyActives } from "@/Api/unities";
import { LoaderSpin } from "@/components/Loaders";
import { useToastNavigate } from "@/utils/toastNavigate";
import { Mask } from "@/utils/mask";
import { cn } from "@/lib/utils";

type Nature = "DEBIT" | "CREDIT";

type UnmatchedEntry = {
  uuid: string;
  nature: Nature;
  counterparty_name: string | null;
  entry_date: string;
  entry_time: string | null;
  amount: number;
  external_id: string;
  similar_transactions: {
    uuid: string;
    description: string;
    amount: number;
    payment_date: string;
  }[];
};

type Option = { id: number; name: string };

const NATURES: Record<
  Nature,
  { label: string; action: string; icon: typeof ArrowUpCircle; active: string }
> = {
  DEBIT: {
    label: "Débitos",
    action: "Lançar como Contas a Pagar",
    icon: ArrowUpCircle,
    active: "bg-blue-600 text-white hover:bg-blue-600",
  },
  CREDIT: {
    label: "Créditos",
    action: "Lançar como Contas a Receber",
    icon: ArrowDownCircle,
    active: "bg-green-600 text-white hover:bg-green-600",
  },
};

export const UnmatchedEntries = ({
  processUuid,
  refreshKey,
  onLaunched,
}: {
  processUuid: string;
  refreshKey: number;
  onLaunched: () => void;
}) => {
  const { toast } = useToastNavigate();
  const [nature, setNature] = useState<Nature>("DEBIT");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<UnmatchedEntry[]>([]);
  const [meta, setMeta] = useState({ last_page: 1, total: 0 });
  const [selected, setSelected] = useState<Record<string, UnmatchedEntry>>({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [units, setUnits] = useState<Option[]>([]);
  const [types, setTypes] = useState<Option[]>([]);
  const [unityId, setUnityId] = useState<string>();
  const [typeId, setTypeId] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getUnmatchedEntries(processUuid, {
        nature,
        page: page.toString(),
        per_page: "100",
        ...(search ? { search } : {}),
      });
      setEntries(data?.response?.data || []);
      setMeta({
        last_page: data?.response?.last_page || 1,
        total: data?.response?.total || 0,
      });
    } catch (error) {
      console.error("Erro ao buscar divergentes:", error);
    } finally {
      setLoading(false);
    }
  }, [processUuid, nature, page, search]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries, refreshKey]);

  const changeNature = (value: Nature) => {
    setNature(value);
    setPage(1);
    setSelected({});
  };

  const selectedList = useMemo(() => Object.values(selected), [selected]);
  const selectedTotal = selectedList.reduce((sum, e) => sum + e.amount, 0);
  const selectedWithSimilar = selectedList.filter(
    (e) => e.similar_transactions?.length > 0
  ).length;
  const allPageSelected =
    entries.length > 0 && entries.every((e) => selected[e.uuid]);

  const toggleOne = (entry: UnmatchedEntry) =>
    setSelected((prev) => {
      const next = { ...prev };
      if (next[entry.uuid]) delete next[entry.uuid];
      else next[entry.uuid] = entry;
      return next;
    });

  const toggleAllPage = () =>
    setSelected((prev) => {
      const next = { ...prev };
      entries.forEach((e) => {
        if (allPageSelected) delete next[e.uuid];
        else next[e.uuid] = e;
      });
      return next;
    });

  const openDialog = async () => {
    setUnityId(undefined);
    setTypeId(undefined);
    setDialogOpen(true);
    try {
      const [unitsResponse, typesResponse] = await Promise.all([
        getSelectUnitiesOnlyActives(),
        nature === "DEBIT" ? selectExpenseTypesData() : selectRevenueTypesData(),
      ]);
      setUnits(unitsResponse.data?.response || []);
      setTypes(typesResponse.data?.response || []);
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
    }
  };

  const submit = async () => {
    if (!unityId || !typeId) return;
    setSubmitting(true);
    try {
      const { data, ok } = await launchUnmatchedEntries(processUuid, {
        entries: selectedList.map((e) => e.uuid),
        unity_id: Number(unityId),
        ...(nature === "DEBIT"
          ? { expense_type_id: Number(typeId) }
          : { revenue_type_id: Number(typeId) }),
      });

      if (!ok) {
        toast({ description: data?.message || "Erro ao lançar." });
        return;
      }

      toast({
        description: data?.message || "Lançamentos criados com sucesso!",
        action: <Check color="#1edc2b" />,
      });
      setDialogOpen(false);
      setSelected({});
      await fetchEntries();
      onLaunched();
    } finally {
      setSubmitting(false);
    }
  };

  const { action } = NATURES[nature];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          {(Object.keys(NATURES) as Nature[]).map((key) => {
            const { label, icon: Icon, active } = NATURES[key];
            return (
              <Button
                key={key}
                variant="outline"
                onClick={() => changeNature(key)}
                className={cn("gap-2", nature === key && active)}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Button>
            );
          })}
        </div>
        <Input
          className="max-w-xs"
          placeholder="Buscar por nome..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-sm text-muted-foreground">
          {meta.total} sem correspondência
          {selectedList.length > 0 &&
            ` · ${selectedList.length} selecionado(s): ${Mask.formatCurrency(selectedTotal)}`}
        </p>
        <Button disabled={selectedList.length === 0} onClick={openDialog}>
          {action}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <LoaderSpin />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={allPageSelected} onCheckedChange={toggleAllPage} />
              </TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Hora</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead>ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  Nenhum lançamento sem correspondência.
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow
                  key={entry.uuid}
                  className={cn(entry.similar_transactions?.length > 0 && "bg-amber-50 dark:bg-amber-900/10")}
                >
                  <TableCell>
                    <Checkbox
                      checked={!!selected[entry.uuid]}
                      onCheckedChange={() => toggleOne(entry)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {entry.counterparty_name || "-"}
                      {entry.similar_transactions?.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                          title={`Possível duplicidade. Já existe em aberto (não conciliado):\n${entry.similar_transactions
                            .map(
                              (t) =>
                                `• ${t.description} - ${Mask.formatCurrency(t.amount)} - ${Mask.transformMaskBirthdayInBr(t.payment_date)}`
                            )
                            .join("\n")}`}
                        >
                          <AlertTriangle className="h-3 w-3" />
                          Possível duplicidade
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {Mask.transformMaskBirthdayInBr(entry.entry_date.slice(0, 10))}
                  </TableCell>
                  <TableCell>{entry.entry_time || "-"}</TableCell>
                  <TableCell className="font-medium">
                    {Mask.formatCurrency(entry.amount)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {entry.external_id}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {meta.last_page > 1 && (
        <div className="flex items-center justify-end gap-2 mt-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Anterior
          </Button>
          <span className="text-sm">
            {page} / {meta.last_page}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= meta.last_page}
            onClick={() => setPage(page + 1)}
          >
            Próxima
          </Button>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{action}</DialogTitle>
            <DialogDescription>
              {selectedList.length} lançamento(s) · {Mask.formatCurrency(selectedTotal)}. Serão
              criados como pagos via PIX e já conciliados.
            </DialogDescription>
          </DialogHeader>

          {selectedWithSimilar > 0 && (
            <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                {selectedWithSimilar} dos {selectedList.length} selecionados já têm um lançamento
                em aberto com mesmo valor e data próxima (marcados como "Possível duplicidade").
                Lançar vai criar um segundo registro. Confira antes de continuar.
              </p>
            </div>
          )}

          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Unidade</label>
              <Select value={unityId} onValueChange={setUnityId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma unidade" />
                </SelectTrigger>
                <SelectContent>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id.toString()}>
                      {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">
                {nature === "DEBIT" ? "Tipo de Despesa" : "Tipo de Receita"}
              </label>
              <Select value={typeId} onValueChange={setTypeId}>
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      nature === "DEBIT" ? "Selecione uma despesa" : "Selecione uma receita"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {types.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button disabled={!unityId || !typeId || submitting} onClick={submit} className="w-full">
              {submitting ? <LoaderSpin /> : "Lançar e conciliar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
